import * as THREE from 'three';
import { SeededRandom } from '../utils/random.js';

/**
 * Spore-style procedural creature meshes.
 *
 * Instead of "primitive + stub limbs", every creature is built from a SPINE:
 * a curved backbone lofted into one continuous body with a varying radius
 * profile (head bulge, fat belly, tapering tail). Limbs are two-segment and
 * jointed (thigh → knee → shin → foot), and species carry parts — beaks,
 * eye stalks, antennae, tail fins — plus stripe/spot skin patterns baked
 * into vertex colors.
 *
 * Conventions: creature local space has +Y up, +Z forward (face direction).
 * The group origin sits on the ground; feet reach y≈0.
 */

/* ══════════════════════════════════════════════════════════════
 *  Geometry helpers
 * ══════════════════════════════════════════════════════════════ */

/**
 * Loft a tube of varying radius along a spine curve, closed with smooth
 * pole caps at both ends. Returns indexed BufferGeometry (smooth normals).
 *
 * @param {THREE.Vector3[]} points  spine control points (head → tail)
 * @param {number[]} radii         radius at each control point
 */
export function sweepSpine(points, radii, lengthSegs = 32, radialSegs = 12) {
  const curve = new THREE.CatmullRomCurve3(points, false, 'catmullrom', 0.5);
  const radiusCurve = radiiInterpolator(radii);
  const frames = curve.computeFrenetFrames(lengthSegs, false);

  const positions = [];
  const indices = [];
  const ringStart = [];

  for (let i = 0; i <= lengthSegs; i++) {
    const t = i / lengthSegs;
    const center = curve.getPointAt(t);
    const N = frames.normals[Math.min(i, frames.normals.length - 1)];
    const B = frames.binormals[Math.min(i, frames.binormals.length - 1)];
    const r = radiusCurve(t);
    ringStart.push(positions.length / 3);
    for (let j = 0; j < radialSegs; j++) {
      const a = (j / radialSegs) * Math.PI * 2;
      const x = center.x + (Math.cos(a) * N.x + Math.sin(a) * B.x) * r;
      const y = center.y + (Math.cos(a) * N.y + Math.sin(a) * B.y) * r;
      const z = center.z + (Math.cos(a) * N.z + Math.sin(a) * B.z) * r;
      positions.push(x, y, z);
    }
  }

  // Side quads
  for (let i = 0; i < lengthSegs; i++) {
    for (let j = 0; j < radialSegs; j++) {
      const a = ringStart[i] + j;
      const b = ringStart[i] + ((j + 1) % radialSegs);
      const c = ringStart[i + 1] + ((j + 1) % radialSegs);
      const d = ringStart[i + 1] + j;
      indices.push(a, b, d, b, c, d);
    }
  }

  // Pole caps — one vertex nudged outward along the tangent at each end
  const headPole = positions.length / 3;
  const p0 = curve.getPointAt(0);
  const tan0 = curve.getTangentAt(0);
  positions.push(
    p0.x - tan0.x * radiusCurve(0) * 0.8,
    p0.y - tan0.y * radiusCurve(0) * 0.8,
    p0.z - tan0.z * radiusCurve(0) * 0.8,
  );
  for (let j = 0; j < radialSegs; j++) {
    indices.push(ringStart[0] + ((j + 1) % radialSegs), ringStart[0] + j, headPole);
  }

  const tailPole = positions.length / 3;
  const p1 = curve.getPointAt(1);
  const tan1 = curve.getTangentAt(1);
  positions.push(
    p1.x + tan1.x * radiusCurve(1) * 0.8,
    p1.y + tan1.y * radiusCurve(1) * 0.8,
    p1.z + tan1.z * radiusCurve(1) * 0.8,
  );
  const last = lengthSegs;
  for (let j = 0; j < radialSegs; j++) {
    indices.push(ringStart[last] + j, ringStart[last] + ((j + 1) % radialSegs), tailPole);
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

/** Smooth (Catmull-style) interpolation over a radius array. */
function radiiInterpolator(radii) {
  return (t) => {
    const f = t * (radii.length - 1);
    const i = Math.min(Math.floor(f), radii.length - 2);
    const u = f - i;
    const r0 = radii[Math.max(i - 1, 0)];
    const r1 = radii[i];
    const r2 = radii[i + 1];
    const r3 = radii[Math.min(i + 2, radii.length - 1)];
    // Catmull-Rom 1D
    return 0.5 * ((2 * r1) + (-r0 + r2) * u
      + (2 * r0 - 5 * r1 + 4 * r2 - r3) * u * u
      + (-r0 + 3 * r1 - 3 * r2 + r3) * u * u * u);
  };
}

/**
 * Organic vertex jitter, hashed by position so seams stay welded.
 * Gentler than the low-poly version — Spore skin is smooth, not faceted.
 */
function jitter(geo, amount, phase) {
  const pos = geo.attributes.position;
  const v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    const h1 = Math.sin(v.x * 51.7 + v.y * 33.3 + v.z * 41.1 + phase);
    const h2 = Math.sin(v.x * 27.9 + v.y * 63.1 + v.z * 31.7 + phase * 1.7);
    const h3 = Math.sin(v.x * 37.3 + v.y * 19.7 + v.z * 53.9 + phase * 2.3);
    pos.setXYZ(i, v.x + h1 * amount, v.y + h2 * amount, v.z + h3 * amount);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
}

/**
 * Bake skin into vertex colors: belly shading + optional stripes or spots
 * in a secondary color, banded along the forward (z) axis like Spore skins.
 */
function bakeSkin(geo, baseColor, accentColor, pattern, patternFreq, phase) {
  geo.computeBoundingBox();
  const { min, max } = geo.boundingBox;
  const spanY = Math.max(max.y - min.y, 1e-5);
  const spanZ = Math.max(max.z - min.z, 1e-5);
  const pos = geo.attributes.position;
  const colors = new Float32Array(pos.count * 3);
  const c = new THREE.Color();
  for (let i = 0; i < pos.count; i++) {
    const ty = (pos.getY(i) - min.y) / spanY;
    const tz = (pos.getZ(i) - min.z) / spanZ;
    c.copy(baseColor);
    if (pattern === 'stripes') {
      const band = Math.sin(tz * patternFreq * Math.PI * 2 + phase);
      if (band > 0.35) c.lerp(accentColor, 0.75);
    } else if (pattern === 'spots') {
      const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
      const n = Math.sin(x * 9.1 + phase) * Math.sin(y * 8.3 + phase * 1.3) * Math.sin(z * 7.7 + phase * 0.7);
      if (n > 0.42) c.lerp(accentColor, 0.7);
    } else if (pattern === 'belly') {
      // Two-tone: lighter underside, like frogs/newts
      if (ty < 0.35) c.lerp(accentColor, 0.6);
    }
    // Top-lit shading bake: bright back, shaded belly
    c.multiplyScalar(0.72 + ty * 0.4);
    colors[i * 3] = c.r; colors[i * 3 + 1] = c.g; colors[i * 3 + 2] = c.b;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
}

/** Capsule between two world points. */
function boneMesh(from, to, radius, material) {
  const dir = new THREE.Vector3().subVectors(to, from);
  const len = dir.length();
  const geo = new THREE.CapsuleGeometry(radius, len, 3, 8);
  const mesh = new THREE.Mesh(geo, material);
  mesh.position.copy(from).addScaledVector(dir, 0.5);
  mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.normalize());
  mesh.castShadow = true;
  return mesh;
}

/* ══════════════════════════════════════════════════════════════
 *  Parts
 * ══════════════════════════════════════════════════════════════ */

/**
 * Two-segment jointed leg built inside a pivot Group anchored at the hip,
 * so the walk cycle can swing the whole limb around the hip joint.
 * Registers itself in `anim.legs` with a gait phase.
 */
function addTwoSegmentLeg(group, anim, mat, hip, side, len, radius, phase, splay = 0.55) {
  const pivot = new THREE.Group();
  pivot.position.copy(hip);

  // Local space: hip at origin
  const kneeL = new THREE.Vector3(side * len * splay, -len * 0.38, len * 0.12);
  const footL = new THREE.Vector3(kneeL.x + side * len * 0.1, -hip.y + radius * 0.7, kneeL.z + len * 0.22);
  pivot.add(boneMesh(new THREE.Vector3(0, 0, 0), kneeL, radius, mat));
  pivot.add(boneMesh(kneeL, footL, radius * 0.8, mat));

  const footGeo = new THREE.SphereGeometry(radius * 1.5, 8, 6);
  footGeo.scale(1.15, 0.55, 1.5);
  const footMesh = new THREE.Mesh(footGeo, mat);
  footMesh.position.set(footL.x, footL.y - radius * 0.15, footL.z + radius * 0.8);
  footMesh.castShadow = true;
  pivot.add(footMesh);

  group.add(pivot);
  anim.legs.push({ pivot, phase });
}

function addEye(group, matEye, matPupil, at, forward, r, stalk = 0, stalkMat = null) {
  let center = at;
  if (stalk > 0 && stalkMat) {
    const tip = at.clone().add(new THREE.Vector3(0, stalk, 0));
    group.add(boneMesh(at, tip, r * 0.35, stalkMat));
    center = tip;
  }
  const eye = new THREE.Mesh(new THREE.SphereGeometry(r, 10, 10), matEye);
  eye.position.copy(center);
  group.add(eye);
  const pupil = new THREE.Mesh(new THREE.SphereGeometry(r * 0.45, 8, 8), matPupil);
  pupil.position.copy(center).addScaledVector(forward, r * 0.68);
  group.add(pupil);
}

function addBeak(group, mat, at, forward, size, openness = 0) {
  const geo = new THREE.ConeGeometry(size * 0.5, size * 1.3, 8);
  geo.rotateX(Math.PI / 2); // cone points +Z
  const beak = new THREE.Mesh(geo, mat);
  beak.position.copy(at).addScaledVector(forward, size * 0.4);
  beak.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), forward);
  beak.rotateX(openness);
  beak.castShadow = true;
  group.add(beak);
}

function addAntenna(group, anim, mat, at, side, len, r) {
  const pivot = new THREE.Group();
  pivot.position.copy(at);
  const mid = new THREE.Vector3(side * len * 0.3, len * 0.6, len * 0.15);
  const tip = mid.clone().add(new THREE.Vector3(side * len * 0.25, len * 0.35, len * 0.2));
  pivot.add(boneMesh(new THREE.Vector3(0, 0, 0), mid, r, mat));
  pivot.add(boneMesh(mid, tip, r * 0.7, mat));
  const bulb = new THREE.Mesh(new THREE.SphereGeometry(r * 2.2, 8, 8), mat);
  bulb.position.copy(tip);
  pivot.add(bulb);
  group.add(pivot);
  anim.antennae.push({ pivot, side });
}

function addTailFin(group, anim, mat, at, size) {
  const pivot = new THREE.Group();
  pivot.position.copy(at);
  const geo = new THREE.ConeGeometry(size * 0.5, size, 4);
  geo.scale(0.25, 1, 1);
  const fin = new THREE.Mesh(geo, mat);
  fin.position.set(0, size * 0.35, -size * 0.2);
  fin.rotation.x = -0.6;
  fin.castShadow = true;
  pivot.add(fin);
  group.add(pivot);
  anim.tail = pivot;
}

/* ══════════════════════════════════════════════════════════════
 *  Body plans
 * ══════════════════════════════════════════════════════════════ */

/**
 * Build a full creature. Same species → same body plan (plan rng is seeded
 * from speciesId + color); per-creature size differences come from `size`.
 *
 * @param {object} data creatureData: { bodyType, size, color, eyeStyle, speciesId, index }
 */
export function buildCreatureMesh(data) {
  const { bodyType, size, color, eyeStyle } = data;
  const planRng = new SeededRandom((data.speciesId ?? 0) * 7919 + ((data.color ?? 0) & 0xffff) + 13);

  // Outer group is what the game positions/orients; everything visual lives
  // in `root` so the animator can bob/squash the whole creature internally.
  const outer = new THREE.Group();
  const group = new THREE.Group();
  outer.add(group);

  /** Animation registry consumed by animateCreature() every frame. */
  const anim = {
    root: group,
    legs: [],
    antennae: [],
    tentacles: [],
    tail: null,
    body: null,
    size,
    isFloater: bodyType === 'floater',
    isSerpent: bodyType === 'serpent',
    phase: planRng.next() * Math.PI * 2,
    gaitRate: 5.5 + planRng.next() * 2.5,
    sing: 0,          // 0–1 pulse, decays after triggerSing()
    skinMat: null,
  };
  outer.userData.anim = anim;

  const baseColor = new THREE.Color(color);
  // Accent skin color: hue-rotated sibling of the body color
  const hsl = {};
  baseColor.getHSL(hsl);
  const accentColor = new THREE.Color().setHSL(
    (hsl.h + 0.45 + planRng.next() * 0.12) % 1,
    Math.min(1, hsl.s * 0.9 + 0.1),
    Math.min(0.85, hsl.l * 1.15),
  );

  const skinMat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.55,
    metalness: 0.02,
    emissive: accentColor,
    emissiveIntensity: 0,   // lit up by the sing pulse
  });
  anim.skinMat = skinMat;
  const limbMat = new THREE.MeshStandardMaterial({
    color: baseColor.clone().multiplyScalar(0.8),
    roughness: 0.6,
  });
  const partMat = new THREE.MeshStandardMaterial({
    color: accentColor.clone().multiplyScalar(0.9),
    roughness: 0.5,
  });
  const eyeMat = new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0x666666, roughness: 0.2 });
  const pupilMat = new THREE.MeshStandardMaterial({ color: 0x14121a, roughness: 0.25 });

  const pattern = planRng.pick(['stripes', 'spots', 'belly', 'none']);
  const patternFreq = 2.5 + planRng.next() * 3;
  const phase = planRng.next() * 10;

  const s = size;
  let spine, radii, legSpec = null, headPos, headForward, bodyLift = 0;

  switch (bodyType) {
    case 'quadruped': {
      // Arched horizontal spine: head raised, fat belly, tapered tail
      const arch = 0.15 + planRng.next() * 0.2;
      spine = [
        new THREE.Vector3(0, s * (0.62 + arch), s * 0.62),   // head
        new THREE.Vector3(0, s * (0.55 + arch * 0.5), s * 0.3),
        new THREE.Vector3(0, s * 0.52, 0),                    // belly
        new THREE.Vector3(0, s * (0.5 + arch * 0.4), -s * 0.34),
        new THREE.Vector3(0, s * (0.62 + arch), -s * 0.66),   // tail root
      ];
      radii = [s * 0.24, s * 0.3, s * (0.34 + planRng.next() * 0.08), s * 0.26, s * 0.1];
      legSpec = { pairs: [[0.28, 1], [0.72, 1]], len: s * 0.52, r: s * 0.07 };
      headPos = spine[0].clone();
      headForward = new THREE.Vector3(0, 0.1, 1).normalize();
      break;
    }
    case 'biped': {
      // Upright S-curve leaning forward — penguin/kiwi stance
      spine = [
        new THREE.Vector3(0, s * 1.18, s * 0.22),  // head
        new THREE.Vector3(0, s * 0.95, s * 0.12),
        new THREE.Vector3(0, s * 0.66, 0),          // belly
        new THREE.Vector3(0, s * 0.42, -s * 0.08),
        new THREE.Vector3(0, s * 0.3, -s * 0.22),   // tail nub
      ];
      radii = [s * 0.22, s * 0.24, s * (0.34 + planRng.next() * 0.06), s * 0.28, s * 0.1];
      legSpec = { pairs: [[0.78, 1]], len: s * 0.42, r: s * 0.07 };
      headPos = spine[0].clone();
      headForward = new THREE.Vector3(0, 0.15, 1).normalize();
      break;
    }
    case 'serpent': {
      // Sinuous horizontal S — raised head, dragging tail
      const w = s * (0.3 + planRng.next() * 0.2);
      spine = [
        new THREE.Vector3(0, s * 0.5, s * 1.0),      // head, raised
        new THREE.Vector3(w, s * 0.26, s * 0.5),
        new THREE.Vector3(-w * 0.7, s * 0.22, 0),
        new THREE.Vector3(w * 0.7, s * 0.2, -s * 0.5),
        new THREE.Vector3(-w * 0.4, s * 0.16, -s * 1.05), // tail tip
      ];
      radii = [s * 0.2, s * 0.24, s * 0.22, s * 0.15, s * 0.04];
      headPos = spine[0].clone();
      headForward = new THREE.Vector3(0, 0.25, 1).normalize();
      break;
    }
    case 'insectoid': {
      // Three-bulge body: head / thorax / abdomen + six legs + antennae
      spine = [
        new THREE.Vector3(0, s * 0.5, s * 0.62),   // head
        new THREE.Vector3(0, s * 0.52, s * 0.24),  // neck pinch
        new THREE.Vector3(0, s * 0.5, 0),          // thorax
        new THREE.Vector3(0, s * 0.54, -s * 0.28), // waist pinch
        new THREE.Vector3(0, s * 0.5, -s * 0.68),  // abdomen
      ];
      radii = [s * 0.2, s * 0.12, s * 0.24, s * 0.13, s * 0.3];
      legSpec = { pairs: [[0.3, 1], [0.48, 1], [0.66, 1]], len: s * 0.5, r: s * 0.045, splay: 0.85 };
      headPos = spine[0].clone();
      headForward = new THREE.Vector3(0, 0, 1);
      break;
    }
    case 'floater': {
      // Balloon jelly with dangling tentacles
      bodyLift = s * 1.0;
      spine = [
        new THREE.Vector3(0, bodyLift + s * 0.55, s * 0.05),
        new THREE.Vector3(0, bodyLift + s * 0.35, 0),
        new THREE.Vector3(0, bodyLift + s * 0.05, 0),
      ];
      radii = [s * 0.18, s * (0.4 + planRng.next() * 0.08), s * 0.3];
      headPos = new THREE.Vector3(0, bodyLift + s * 0.25, s * 0.32);
      headForward = new THREE.Vector3(0, 0, 1);
      break;
    }
    case 'blob':
    default: {
      // Fat puddle with a face — mostly body
      spine = [
        new THREE.Vector3(0, s * 0.42, s * 0.3),
        new THREE.Vector3(0, s * 0.4, 0),
        new THREE.Vector3(0, s * 0.36, -s * 0.3),
      ];
      radii = [s * 0.3, s * (0.42 + planRng.next() * 0.08), s * 0.28];
      headPos = new THREE.Vector3(0, s * 0.52, s * 0.5);
      headForward = new THREE.Vector3(0, 0.2, 1).normalize();
      break;
    }
  }

  // ── Body ──
  const bodyGeo = sweepSpine(spine, radii);
  jitter(bodyGeo, s * 0.018, planRng.next() * 100);
  bakeSkin(bodyGeo, baseColor, accentColor, pattern, patternFreq, phase);
  const body = new THREE.Mesh(bodyGeo, skinMat);
  body.castShadow = true;
  group.add(body);
  anim.body = body;

  // ── Legs (diagonal gait: opposite corners share a phase) ──
  if (legSpec) {
    const curve = new THREE.CatmullRomCurve3(spine, false, 'catmullrom', 0.5);
    legSpec.pairs.forEach(([t, active], pairIdx) => {
      if (!active) return;
      const p = curve.getPointAt(t);
      const hw = radiiInterpolator(radii)(t) * 0.7;
      const hipL = new THREE.Vector3(-hw, p.y, p.z);
      const hipR = new THREE.Vector3(hw, p.y, p.z);
      const phaseL = ((pairIdx % 2) === 0 ? 0 : Math.PI);
      addTwoSegmentLeg(group, anim, limbMat, hipL, -1, legSpec.len, legSpec.r, phaseL, legSpec.splay);
      addTwoSegmentLeg(group, anim, limbMat, hipR, 1, legSpec.len, legSpec.r, phaseL + Math.PI, legSpec.splay);
    });
  }

  // ── Floater tentacles (each on a pivot so they undulate) ──
  if (bodyType === 'floater') {
    const n = 2 + planRng.int(0, 2);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + 0.4;
      const pivot = new THREE.Group();
      pivot.position.set(Math.cos(a) * s * 0.24, bodyLift + s * 0.02, Math.sin(a) * s * 0.24);
      const mid = new THREE.Vector3(Math.cos(a) * s * 0.1, -s * 0.42, Math.sin(a) * s * 0.1);
      const tip = mid.clone().add(new THREE.Vector3(-Math.cos(a) * s * 0.08, -s * 0.34, -Math.sin(a) * s * 0.08));
      pivot.add(boneMesh(new THREE.Vector3(0, 0, 0), mid, s * 0.035, limbMat));
      pivot.add(boneMesh(mid, tip, s * 0.025, limbMat));
      group.add(pivot);
      anim.tentacles.push({ pivot, idx: i });
    }
  }

  // ── Face ──
  const right = new THREE.Vector3(1, 0, 0);
  const eyeR = s * 0.085;
  const useStalks = bodyType === 'blob' || (planRng.next() < 0.3 && bodyType !== 'floater');
  const stalkLen = useStalks ? s * (0.18 + planRng.next() * 0.15) : 0;

  const eyeBase = headPos.clone().addScaledVector(headForward, s * 0.05);
  if (eyeStyle === 'single') {
    addEye(group, eyeMat, pupilMat, eyeBase.clone().add(new THREE.Vector3(0, s * 0.08, 0)), headForward, eyeR * 1.5, stalkLen, partMat);
  } else if (eyeStyle === 'triple') {
    addEye(group, eyeMat, pupilMat, eyeBase.clone().add(new THREE.Vector3(0, s * 0.14, 0)), headForward, eyeR * 0.8, stalkLen * 0.7, partMat);
    addEye(group, eyeMat, pupilMat, eyeBase.clone().addScaledVector(right, -s * 0.13), headForward, eyeR, stalkLen, partMat);
    addEye(group, eyeMat, pupilMat, eyeBase.clone().addScaledVector(right, s * 0.13), headForward, eyeR, stalkLen, partMat);
  } else if (eyeStyle === 'compound') {
    for (let i = -1; i <= 1; i++) {
      addEye(group, eyeMat, pupilMat, eyeBase.clone().addScaledVector(right, i * s * 0.09).add(new THREE.Vector3(0, Math.abs(i) * -s * 0.03 + s * 0.06, 0)), headForward, eyeR * 0.62, 0, null);
    }
  } else if (eyeStyle !== 'none') { // 'double' default
    addEye(group, eyeMat, pupilMat, eyeBase.clone().addScaledVector(right, -s * 0.11).add(new THREE.Vector3(0, s * 0.06, 0)), headForward, eyeR, stalkLen, partMat);
    addEye(group, eyeMat, pupilMat, eyeBase.clone().addScaledVector(right, s * 0.11).add(new THREE.Vector3(0, s * 0.06, 0)), headForward, eyeR, stalkLen, partMat);
  }

  // Mouth: beak for most walkers, none for floaters/serpents sometimes
  if (bodyType !== 'floater' && planRng.next() < 0.8) {
    const mouthAt = headPos.clone().addScaledVector(headForward, s * 0.1).add(new THREE.Vector3(0, -s * 0.08, 0));
    addBeak(group, partMat, mouthAt, headForward, s * (0.16 + planRng.next() * 0.12), planRng.next() * 0.15);
  }

  // Antennae: always for insectoids, sometimes elsewhere
  if (bodyType === 'insectoid' || planRng.next() < 0.25) {
    const at = headPos.clone().add(new THREE.Vector3(0, s * 0.1, 0));
    addAntenna(group, anim, partMat, at.clone().addScaledVector(right, -s * 0.08), -1, s * 0.3, s * 0.02);
    addAntenna(group, anim, partMat, at.clone().addScaledVector(right, s * 0.08), 1, s * 0.3, s * 0.02);
  }

  // Tail fin for serpents and some quadrupeds
  if (bodyType === 'serpent' || (bodyType === 'quadruped' && planRng.next() < 0.4)) {
    addTailFin(group, anim, partMat, spine[spine.length - 1].clone(), s * 0.3);
  }

  return outer;
}

/* ══════════════════════════════════════════════════════════════
 *  Procedural animation
 * ══════════════════════════════════════════════════════════════ */

/**
 * Drive a creature's idle/walk animation. Call once per frame.
 *
 * @param {THREE.Group} outer  group returned by buildCreatureMesh
 * @param {number} time        elapsed seconds
 * @param {number} speed01     0 = idle, 1 = full walk (scales gait amplitude)
 */
export function animateCreature(outer, time, speed01 = 0) {
  const a = outer.userData.anim;
  if (!a) return;
  const t = time + a.phase;

  // Sing pulse decay (~0.45s per full pulse)
  if (a.sing > 0) {
    const dt = a._lastT != null ? Math.min(0.1, Math.max(0, time - a._lastT)) : 0;
    a.sing = Math.max(0, a.sing - dt * 2.2);
  }
  a._lastT = time;

  // Walk cycle: legs swing around their hip pivots, diagonal pairs in sync
  for (const leg of a.legs) {
    leg.pivot.rotation.x = Math.sin(t * a.gaitRate + leg.phase) * 0.55 * speed01;
  }

  // Whole-body motion
  if (a.isFloater) {
    // Continuous hover bob, movement or not
    a.root.position.y = Math.sin(t * 1.3) * 0.1 * a.size;
  } else if (a.legs.length > 0) {
    // Step bounce while walking (double the gait: one bounce per step)
    a.root.position.y = Math.abs(Math.sin(t * a.gaitRate + Math.PI / 4)) * 0.05 * a.size * speed01;
  } else if (a.isSerpent) {
    // Slither: gentle lateral sway that grows with speed
    a.root.rotation.y = Math.sin(t * 4) * (0.04 + 0.08 * speed01);
  }

  // Breathing squash — subtle, always on; singing puffs the body up
  if (a.body) {
    const b = Math.sin(t * 2.1) * 0.015;
    const p = 1 + a.sing * 0.14;
    a.body.scale.set((1 + b) * p, (1 - b * 0.7) * p, (1 + b) * p);
  }

  // Skin glows with the call
  if (a.skinMat) a.skinMat.emissiveIntensity = a.sing * 0.65;

  // Tail sway
  if (a.tail) {
    a.tail.rotation.y = Math.sin(t * 2.4) * (0.15 + 0.2 * speed01);
  }

  // Antenna wobble
  for (const ant of a.antennae) {
    ant.pivot.rotation.z = ant.side * 0.12 + Math.sin(t * 3.2 + ant.side) * 0.14;
  }

  // Tentacle undulation
  for (const ten of a.tentacles) {
    ten.pivot.rotation.x = Math.sin(t * 1.9 + ten.idx * 1.7) * 0.22;
    ten.pivot.rotation.z = Math.cos(t * 1.6 + ten.idx * 1.3) * 0.18;
  }
}

/**
 * Kick off a sing pulse — the body puffs and the skin glows, decaying
 * over ~half a second. Call when the creature's note actually plays.
 */
export function triggerSing(outer, strength = 1) {
  const a = outer?.userData?.anim;
  if (!a) return;
  a.sing = Math.max(a.sing, Math.min(1, strength));
}
